import { InjectReactive } from "vue-property-decorator";
import { defineAsyncComponent } from 'vue';
import Component, { mixins } from 'vue-class-component';
import { Getter } from "vuex-class";
import Dictionary from "@/types/Dictionary";
import VueGridLayout from 'vue-grid-layout';
import { VueGridLayoutUtils } from '@/components/commons/Utils';
import WidgetLibrary from "@/components/commons/Dashboard/WidgetLibrary.vue";
import tWidgetLibrary from '@/types/Dashboard/WidgetLibrary';
import WidgetTypeEnum from "@/enums/WidgetTypeEnum";
import IParameters from "@/interfaces/IParameters";
import BaseDashboard from "@/types/Dashboard/BaseDashboard";
import BaseWidget from "@/types/Dashboard/BaseWidget";
import DashboardTypeEnum from "@/enums/DashboardTypeEnum";
import { StatusCodes } from "http-status-codes";

/** Largeur par défaut d'un widget */
const DEFAULT_WIDTH: number = 3;
/** Hauteur par défaut d'un widget */
const DEFAULT_HEIGHT: number = 6;

/** Position de la souris lors du drag'n'drop lors de l'ajout de widget */
let mouseXY = { "x": null, "y": null };
/** Positionnement du rectangle rouge de drag'n'drop lors de l'ajout de widget */
let dragPos = {"x": null, "y": null, "w": DEFAULT_WIDTH, "h": DEFAULT_HEIGHT, "i": null};

/**
 * Base commune à tous les tableaux de bord (dashboards) manipulant un VueGridLayout
 * @see https://jbaysolutions.github.io/vue-grid-layout/
 * @template T Type de tableau de bord héritant de BaseDashboard<BaseWidget> à instancier
 */
@Component({
	components: {
		GridLayout: VueGridLayout.GridLayout,
		GridItem: VueGridLayout.GridItem,
		/** Container de chargement des widgets */
		WidgetContainer: defineAsyncComponent(() => import("@/components/commons/Dashboard/WidgetContainer.vue")),
		WidgetLibrary
	}
})
export default class Dashboard<T extends BaseDashboard<BaseWidget>> extends mixins(VueGridLayoutUtils) {
	/** Pour rendre accessible l'enum dans la section template */
	protected widgetTypeEnum = WidgetTypeEnum;
	protected dashboardTypeEnum = DashboardTypeEnum;

	/** Variant de couleur à utiliser sur le tableau de bord */
	@InjectReactive()
	public Variant!: string;

	/** Méta-données d'un type de widget (bibliothèque de widgets) */
	@Getter('WidgetLibraryStore/GetWidgetLibrary')
	protected widgetLibrary: (widgetType: string)=> tWidgetLibrary;

	/** Type de tableau de bord en cours */
	protected dashboardType: DashboardTypeEnum = DashboardTypeEnum.Main;

	/** Tableau de bord local synchronisé avec le vue-grid-layout */
	protected lDashboard: T = {} as T;

	/** widget sélectionné pour ajout */
	protected selectedWidget: tWidgetLibrary = new tWidgetLibrary();

	/** Nombre de colonnes sur le composant grid-layout */
	protected colNum: number = 12;

	/** Affichage ou non de la bibliothèque de widgets */
	protected displayWidgetsLib: boolean = false;

	/** Indique si le tableau de bord est automatiquement sauvegardé ou non après l'ajout ou la suppression d'un widget */
	protected autoSave: boolean = false;

	/** Indique si prévisualisation du dashboard ou non */
	protected preview: boolean = false;

	//#region Gestion ajout/suppression d'un widget

	/** Ajout d'un widget sur le tableau de bord
	 * @param onDrop Indique s'il l'ajout est lors de la fin d'un drag&drop ou non
	 */
	protected async addWidget(onDrop: boolean = false): Promise<boolean> {
		console.debug("Dashboard - addWidget() - onDrop : ", onDrop);

		await this.$store.dispatch("ShowLoadingMask", this.$t('LoadingAction')); 

		if (this.selectedWidget) {
			let maxY = 0;
			let maxI = 0;

			// Gestion placement du widget à ajouter par rapport aux widgets déjà présent
			if (this.lDashboard.Widgets && this.lDashboard.Widgets.length > 0) {
				// Obtention de l'ordonnée max pour décaler le nouveau widget à côté
				maxY = Math.max(...this.lDashboard.Widgets.map(o => o.y));

				// Obtention du dernier index de widget pour prendre le suivant
				maxI = Math.max(...this.lDashboard.Widgets.map(o => Number(o.i)));
				maxI++;
			}
			
			let dashboardType: string = DashboardTypeEnum[this.dashboardType].toString();
			this.$store.dispatch(dashboardType + "DashboardStore/SetTempObjectId")
				.then(async response => {

					let tempDashboard: T = JSON.parse(JSON.stringify(this.lDashboard));

					tempDashboard.Widgets.push({
						Id: this.$store.getters[dashboardType + "DashboardStore/GetTempObjectId"],
						Name: this.selectedWidget.Label,					// Affiche le texte correspondant à la valeur de l'item de widgetLibrary trouvé
						Type: WidgetTypeEnum[this.selectedWidget.Name],
						Parameters: null,	// {} as IParameters
						x: (onDrop ? dragPos.x : (tempDashboard.Widgets.length * 2) % (this.colNum || 12)),
						y: (onDrop ? dragPos.y : maxY + 1),
						w: DEFAULT_WIDTH,
						h: DEFAULT_HEIGHT,
						i: maxI.toString(),
						minH: 1,
						maxH: Infinity,
						InError: false
					});

					// Sauvegarde du tableau de bord local
					if (this.autoSave)
						await this.$store.dispatch(dashboardType +'DashboardStore/Save', { dashboard: tempDashboard });

				})
				.catch(err => {
					if (err.response.status ===  StatusCodes.CONFLICT) {
						this.$bvToast.toast(this.$t('SaveConflictError').toString(), { // $t('AnErrorOccured') / "Une erreur s'est produite."
							title: this.$t('Error').toString(), // $t('Error') / Erreur
							autoHideDelay: 10000,
							variant: "danger",
							solid: true,
							appendToast: true,
							toaster: "b-toaster-top-center"
						});
					}else{
						console.error(err);
						this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // $t('AnErrorOccured') / "Une erreur s'est produite."
							title: this.$t('Error').toString(), // $t('Error') / Erreur
							autoHideDelay: 10000,
							variant: "danger",
							solid: true,
							appendToast: true,
							toaster: "b-toaster-top-center"
						});
					}
				}).finally( async () =>{
            		await this.$store.dispatch("HideLoadingMask", {});
				});
		}
		return true;
	}

	/** 
	 * Suppression d'un widget sur le tableau de bord
	 * @param widget widget à supprimer
	 * */
	protected removeWidget(widget) {
		console.debug("Dashboard - removeWidget : %O", widget);

		const index = this.lDashboard.Widgets.map(item => item.i).indexOf(widget);
		this.lDashboard.Widgets.splice(index, 1);

		// Sauvegarde du tableau de bord local
		if (this.autoSave) {
			let dashboardType: string = DashboardTypeEnum[this.dashboardType].toString();
			this.$store.dispatch(dashboardType +'DashboardStore/Save', { dashboard: this.lDashboard })
				.catch(err => {
					console.error(err);
					this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // $t('AnErrorOccured') / "Une erreur s'est produite."
						title: this.$t('Error').toString(), // $t('Error') / Erreur
						autoHideDelay: 10000,
						variant: "danger",
						solid: true,
						appendToast: true,
						toaster: "b-toaster-top-center"
					});
				});
		}
	}

	//#endregion Gestion ajout/suppression d'un widget

	//#region Gestion du drag'n'drop

	/** Réinitialise le positionnement de la souris et de la position de drag */
	protected dragstart(e) {
		mouseXY = { "x": null, "y": null };
		dragPos = {"x": null, "y": null, "w": DEFAULT_WIDTH, "h": DEFAULT_HEIGHT, "i": null};
	}

	/**
	 * Drag d'un nouveau widget à ajouter (tant que le bouton gauche de la souris n'est pas relaché)
	 * @see https://jbaysolutions.github.io/vue-grid-layout/guide/10-drag-from-outside.html
	 * @see https://github.com/jbaysolutions/vue-grid-layout/blob/master/website/docs/.vuepress/components/Example10DragFromOutside.vue
	 *  */
	protected drag(e) {
		/** Récupère le noeud dans le Dom contenant le VueGridLayout cible */
		let parentRect: DOMRect = document.getElementById(DashboardTypeEnum[this.dashboardType].toString() + this.lDashboard.Id).getBoundingClientRect();
		
		/** Récupère la ref vers le VueGridLayout cible */
		let gridlayout = (this.$refs[DashboardTypeEnum[this.dashboardType].toString() + this.lDashboard.Id] as any);

		if (parentRect && gridlayout) {
			let mouseInGrid: boolean = false;

			/** Détecte si la souris est dans la grille du tableau de bord */
			if (((mouseXY.x > parentRect.left) && (mouseXY.x < parentRect.right)) && ((mouseXY.y > parentRect.top) && (mouseXY.y < parentRect.bottom)))
				mouseInGrid = true;

			/** SI la souris est dans grille et ne contient pas de widget avec un i contenant 'drop' ALORS on ajoute le widget dans le tableau de bord local */
			if (mouseInGrid && (this.lDashboard.Widgets.findIndex(item => item.i === 'drop')) === -1) {
				this.lDashboard.Widgets.push({
					Id: 0,
					Name: 'drop',
					Type: undefined,
					Parameters: {} as IParameters,
					x: (this.lDashboard.Widgets.length * 2) % (this.colNum || 12),
					y: this.lDashboard.Widgets.length + (this.colNum || 12), 			// puts it at the bottom,
					w: DEFAULT_WIDTH,
					h: DEFAULT_HEIGHT,
					i: 'drop',
					minH: 1,
					maxH: Infinity,
					InError: false
				});
			}
			
			/** On recherche l'index du widget contenant i avec 'drop' */
			let index = this.lDashboard.Widgets.findIndex(item => item.i === 'drop');

			/** Si le widget temporaire existe */
			if (index !== -1) {
				try {
					/** Applique un style d'affichage à none à tous les widgets */
					gridlayout.$children[this.lDashboard.Widgets.length].$refs.item.style.display = "none";
				}
				catch {
				}
				let el = gridlayout.$children[index];
				el.dragging = {"top": mouseXY.y - parentRect.top, "left": mouseXY.x - parentRect.left};
				let new_pos = el.calcXY(mouseXY.y - parentRect.top, mouseXY.x - parentRect.left);
				if (mouseInGrid) {
					gridlayout.dragEvent('dragstart', 'drop', new_pos.x, new_pos.y, DEFAULT_HEIGHT, DEFAULT_WIDTH);
					dragPos.i = String(index);
					dragPos.x = this.lDashboard.Widgets[index].x;
					dragPos.y = this.lDashboard.Widgets[index].y;
				}
				else {
					gridlayout.dragEvent('dragend', 'drop', new_pos.x, new_pos.y, DEFAULT_HEIGHT, DEFAULT_WIDTH);
					this.lDashboard.Widgets = this.lDashboard.Widgets.filter(obj => obj.i !== 'drop');
				}
			}
		}
	}

	/**
	 * Fin de drag&drop lors de l'ajout d'un widget
	 * @see https://jbaysolutions.github.io/vue-grid-layout/guide/10-drag-from-outside.html
	 * @see https://github.com/jbaysolutions/vue-grid-layout/blob/master/website/docs/.vuepress/components/Example10DragFromOutside.vue
	 * */
	protected dragend(e) {
		let parentRect: DOMRect = document.getElementById(DashboardTypeEnum[this.dashboardType].toString() + this.lDashboard.Id).getBoundingClientRect();

		let gridlayout = (this.$refs[DashboardTypeEnum[this.dashboardType].toString() + this.lDashboard.Id] as any);

		/** Si la souris est dans la grille */
		if ((parentRect && gridlayout) && ((mouseXY.x > parentRect.left) && (mouseXY.x < parentRect.right)) && ((mouseXY.y > parentRect.top) && (mouseXY.y < parentRect.bottom))) {
			gridlayout.dragEvent('dragend', 'drop', dragPos.x, dragPos.y, 1, 1); // DEFAULT_HEIGHT , DEFAULT_WIDTH);
			this.lDashboard.Widgets = this.lDashboard.Widgets.filter(obj => obj.i !== 'drop');
			
			this.addWidget(true);

			// Ligne ci-dessous issue de l'exemple de https://github.com/jbaysolutions désactivée car elle provoquait un décalage si un widget était placé juste en-dessous du widget déposé
			//gridlayout.dragEvent('dragend', dragPos.i, dragPos.x, dragPos.y, 1, 1); //DEFAULT_HEIGHT, DEFAULT_WIDTH);
			try {
				gridlayout.$children[this.lDashboard.Widgets.length].$refs.item.style.display="block";
			} catch {
			}
		}
	}

	//#endregion Gestion du drag'n'drop

	/** 
	 * Bloque le dimensionnement vertical d'un VueGridItem sur son redimenssionnement
	 * @description A utiliser sur un évènement 'resized' de VueGridItem
	 * @see https://github.com/jbaysolutions/vue-grid-layout/issues/221
	 */
	protected gridItemResized(i, newH, newW, newHPx, newWPx){
		console.debug("Dashboard - Event @resized - gridItemResized()1 - ref widget : " + this.$refs[this.dashboardType.toString() + this.lDashboard.Id + 'widget' + i] + "[0]._props : %O - " +
		 " - newH : %O - newW : %O - newHPx : %O - newWPx : %O", (this.$refs[this.dashboardType.toString() + this.lDashboard.Id + 'widget' + i] as any)[0]._props, newH , newW, newHPx, newWPx);
		
		this.resizeGridItem((this.$refs[this.dashboardType.toString() + this.lDashboard.Id + 'widget' + i] as any)[0]);
	}

	/** Renvoi la hauteur à laquelle apparait le tableau de bord
	 * @description Utilisé pour placer le top de la sidebar de la WidgetLibrary à la hauteur du tableau de bord
	 * @param parentElementId Id de l'élement parent à rechercher
	 */
	protected getTop(parentElementId) {
		let parentEl:HTMLElement = document.getElementById(parentElementId);
		if (parentEl) {
			let parentRect: DOMRect = parentEl.getBoundingClientRect();
			if (parentRect)
				return parentRect.top + 'px';
		}
	}

	protected mounted() {
		/** Ajout d'un évènement pour gérer le drag'n'drop d'ajout de widget de puis la bibliothèque
		 * Ajouter un test sur this.$screen.touch pour activer le EventListener sur la souris ou le touchscreen en fonction si l'écran est tactile ou non ? Non, car les Pc à écran tactile ne détecterait alors plus
		 */
		// if(this.$screen.touch == true) {
			document.addEventListener("touchmove", function (e) {
				mouseXY.x = e.touches[0].clientX;
				mouseXY.y = e.touches[0].clientY;
			});
		// }
		// else {
			document.addEventListener("dragover", function (e) {
				mouseXY.x = e.clientX;
				mouseXY.y = e.clientY;
			}, false);
		// }
	}
}